import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Globe, Loader2, Mail, Send } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const serviceOptions = [
  "استفسار عام",
  "التحول الرقمي المؤسسي",
  "بناء منصة SaaS",
  "دمج الذكاء الاصطناعي",
  "بوابات الدفع الإلكتروني",
  "استشارات PMO",
  "الأمن السيبراني و PDPL",
];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "استفسار عام", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/inquiries`, {
        ...form,
        email: form.email || null,
      });
      setSent(true);
      toast.success("تم استلام طلبك بنجاح — سنعاود التواصل معك قريباً");
      setForm({ name: "", phone: "", email: "", service: "استفسار عام", message: "" });
    } catch (err) {
      toast.error("تعذّر إرسال الطلب، حاول مرة أخرى أو راسلنا على البريد");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-2xl bg-[#0F1A36]/80 border border-[#D4AF37]/15 px-5 py-3.5 font-body text-[#F8FAFC] placeholder:text-[#64748B] focus:outline-none focus:border-[#D4AF37]/60 transition-colors duration-300";

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 sm:py-32 bg-[#0B132B] overflow-hidden">
      <div className="absolute top-0 -left-40 w-[460px] h-[460px] rounded-full blur-[150px] opacity-15 bg-[#D4AF37]" aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5"
        >
          <div className="font-mono-tech text-xs sm:text-sm tracking-[0.25em] text-[#D4AF37] mb-4" dir="ltr">— CONTACT US</div>
          <h2 className="font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl text-[#F8FAFC]" data-testid="contact-title">
            لنحوّل فكرتك إلى <span className="gold-text">منتج يعمل</span>
          </h2>
          <p className="mt-5 font-body text-base sm:text-lg leading-relaxed text-[#94A3B8]">
            أخبرنا عن مشروعك وسنعاود التواصل معك لجلسة اكتشاف مجانية — نفهم الهدف والجمهور ومعايير النجاح،
            ثم نقدّم نطاقاً وسعراً نهائياً قبل البدء.
          </p>
          <div className="mt-9 space-y-4">
            <a
              href="mailto:info@brah-tech.com"
              data-testid="contact-email-link"
              className="flex items-center gap-4 glass-card rounded-2xl px-5 py-4"
            >
              <span className="w-11 h-11 rounded-xl bg-[#D4AF37]/15 border border-[#D4AF37]/35 flex items-center justify-center shrink-0">
                <Mail size={20} className="text-[#E5C158]" />
              </span>
              <div>
                <div className="font-display font-bold text-[#F8FAFC]" dir="ltr">info@brah-tech.com</div>
                <div className="text-sm text-[#94A3B8] font-body">البريد الرسمي للمكتب</div>
              </div>
            </a>
            <div className="flex items-center gap-4 glass-card rounded-2xl px-5 py-4" data-testid="contact-domain-card">
              <span className="w-11 h-11 rounded-xl bg-[#38BDF8]/15 border border-[#38BDF8]/35 flex items-center justify-center shrink-0">
                <Globe size={20} className="text-[#38BDF8]" />
              </span>
              <div>
                <div className="font-display font-bold text-[#F8FAFC]" dir="ltr">www.brah-tech.com</div>
                <div className="text-sm text-[#94A3B8] font-body">نطاقنا الرسمي — الرقم الوطني الموحد 7054088864</div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-7"
        >
          <form onSubmit={submit} className="glass-card rounded-3xl p-7 sm:p-10 space-y-5" data-testid="contact-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block mb-2 font-body text-sm text-[#CBD5E1]" htmlFor="name">الاسم الكامل *</label>
                <input id="name" data-testid="contact-name-input" required minLength={2} className={inputCls} placeholder="مثال: محمد العتيبي" value={form.name} onChange={set("name")} />
              </div>
              <div>
                <label className="block mb-2 font-body text-sm text-[#CBD5E1]" htmlFor="phone">رقم الجوال *</label>
                <input id="phone" data-testid="contact-phone-input" required minLength={8} dir="ltr" className={`${inputCls} text-left`} placeholder="05xxxxxxxx" value={form.phone} onChange={set("phone")} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block mb-2 font-body text-sm text-[#CBD5E1]" htmlFor="email">البريد الإلكتروني</label>
                <input id="email" data-testid="contact-email-input" type="email" dir="ltr" className={`${inputCls} text-left`} placeholder="you@company.com" value={form.email} onChange={set("email")} />
              </div>
              <div>
                <label className="block mb-2 font-body text-sm text-[#CBD5E1]" htmlFor="service">نوع الخدمة</label>
                <select id="service" data-testid="contact-service-select" className={inputCls} value={form.service} onChange={set("service")}>
                  {serviceOptions.map((o) => <option key={o} value={o} className="bg-[#0F1A36]">{o}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block mb-2 font-body text-sm text-[#CBD5E1]" htmlFor="message">تفاصيل المشروع *</label>
              <textarea id="message" data-testid="contact-message-input" required minLength={5} rows={5} className={`${inputCls} resize-none`} placeholder="اشرح فكرتك أو احتياجك باختصار…" value={form.message} onChange={set("message")} />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-10 py-4 font-display font-bold text-[#060B19] hover:bg-[#E5C158] hover:shadow-[0_0_40px_rgba(212,175,55,0.35)] disabled:opacity-60 transition-all duration-300"
            >
              {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              {sending ? "جارٍ الإرسال…" : sent ? "أرسل طلباً آخر" : "أرسل الطلب"}
            </button>
            {sent && (
              <p className="font-body text-sm text-[#10B981]" data-testid="contact-success-message">
                وصلنا طلبك بنجاح — سنتواصل معك خلال يوم عمل.
              </p>
            )}
          </form>
        </motion.div>
      </div>
    </section>
  );
};
